import { useState } from 'react';
import { Card, Col, DatePicker, Empty, Row, Spin, Statistic, Table, Tag, Tooltip } from 'antd';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { formatNumber, handleAPIError } from '../../utils/helpers';

interface WrittenOffRow {
	device_id: number;
	imei: string | null;
	phone_model: string;
	customer: string | null;
	phone_number: string | null;
	sold_by: string | null;
	written_off_at: string;
	written_off_by: string | null;
	reason: string;
	written_off_amount: string;
	principal: string;
	repaid: string;
	resale: string;
	capital_lost: string;
	profit_forgone: string;
	recovered_capital: boolean;
	is_recovered: boolean;
}

interface Report {
	rows: WrittenOffRow[];
	totals: {
		contracts: number;
		written_off_amount: number;
		principal: number;
		repaid: number;
		resale: number;
		capital_lost: number;
		profit_forgone: number;
		recovered_capital: number;
		lost_money: number;
	};
}

const { RangePicker } = DatePicker;
const money = (v: string | number) => formatNumber(Math.round(Number(v)));

export default function WrittenOffReport() {
	const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);

	const start = range?.[0].format('YYYY-MM-DD');
	const end = range?.[1].format('YYYY-MM-DD');

	const { data, isLoading, error } = useQuery<Report>({
		queryKey: ['written-off', start, end],
		queryFn: async () => {
			const res = await axios.get('/admin/reports/written-off', {
				params: start && end ? { start, end } : {},
				withCredentials: true,
			});
			return res.data.results;
		},
	});
	if (error) handleAPIError(error);

	const t = data?.totals;

	const columns = [
		{
			title: 'Contract',
			width: 210,
			render: (r: WrittenOffRow) => (
				<div>
					<div style={{ fontWeight: 500 }}>
						#{r.device_id} · {r.phone_model}
					</div>
					<div style={{ fontSize: 12, color: '#888' }}>
						{r.customer}
						{r.phone_number ? ` · ${r.phone_number}` : ''}
					</div>
				</div>
			),
		},
		{
			title: 'Written off',
			align: 'right' as const,
			width: 120,
			sorter: (a: WrittenOffRow, b: WrittenOffRow) =>
				Number(a.written_off_amount) - Number(b.written_off_amount),
			render: (r: WrittenOffRow) => money(r.written_off_amount),
		},
		{
			title: <Tooltip title="Cash Zazu funded on this contract">Our money in</Tooltip>,
			align: 'right' as const,
			width: 120,
			render: (r: WrittenOffRow) => money(r.principal),
		},
		{
			title: <Tooltip title="Installments paid, plus anything a resale brought back">Came back</Tooltip>,
			align: 'right' as const,
			width: 120,
			render: (r: WrittenOffRow) => {
				const back = Number(r.repaid) + Number(r.resale);
				return back ? money(back) : <span style={{ color: '#ccc' }}>—</span>;
			},
		},
		{
			title: 'Capital lost',
			align: 'right' as const,
			width: 130,
			defaultSortOrder: 'descend' as const,
			sorter: (a: WrittenOffRow, b: WrittenOffRow) =>
				Number(a.capital_lost) - Number(b.capital_lost),
			render: (r: WrittenOffRow) =>
				r.recovered_capital ? (
					<Tag color="success">made it back</Tag>
				) : (
					<strong style={{ color: '#cf1322' }}>{money(r.capital_lost)}</strong>
				),
		},
		{
			title: <Tooltip title="Interest that was never earned">Profit forgone</Tooltip>,
			align: 'right' as const,
			width: 120,
			render: (r: WrittenOffRow) => (
				<span style={{ color: '#888' }}>{money(r.profit_forgone)}</span>
			),
		},
		{
			title: 'Written off',
			width: 160,
			render: (r: WrittenOffRow) => (
				<div>
					<div>{dayjs(r.written_off_at).format('DD MMM YYYY')}</div>
					<div style={{ fontSize: 12, color: '#888' }}>{r.written_off_by}</div>
				</div>
			),
		},
		{
			title: 'Reason',
			dataIndex: 'reason',
			render: (v: string, r: WrittenOffRow) => (
				<span>
					{v}
					{r.is_recovered && (
						<Tag color="blue" style={{ marginLeft: 6 }}>
							recovered
						</Tag>
					)}
				</span>
			),
		},
	];

	return (
		<AppLayout title="Written Off" subtitle="Contracts given up on, and what they cost">
			<div style={{ padding: 16 }}>
				<Card size="small" style={{ marginBottom: 16 }}>
					<Row gutter={16} align="middle">
						<Col>
							<RangePicker
								value={range}
								onChange={(v) => setRange(v as [Dayjs, Dayjs] | null)}
								placeholder={['All time', '']}
							/>
						</Col>
						{t && (
							<>
								<Col flex="auto" />
								<Col>
									<Statistic title="Contracts" value={t.contracts} />
								</Col>
								<Col>
									<Statistic
										title="Written off"
										value={money(t.written_off_amount)}
										prefix="UGX"
									/>
								</Col>
								<Col>
									<Statistic
										title="Capital lost"
										value={money(t.capital_lost)}
										prefix="UGX"
										valueStyle={{ color: '#cf1322' }}
									/>
								</Col>
								<Col>
									<Statistic
										title="Profit forgone"
										value={money(t.profit_forgone)}
										prefix="UGX"
										valueStyle={{ color: '#8c8c8c' }}
									/>
								</Col>
								<Col>
									<Statistic
										title="Made our money back"
										value={`${t.recovered_capital} of ${t.contracts}`}
										valueStyle={{
											color: t.recovered_capital ? '#3f8600' : undefined,
										}}
									/>
								</Col>
							</>
						)}
					</Row>
				</Card>

				{isLoading ? (
					<div style={{ padding: 48, textAlign: 'center' }}>
						<Spin />
					</div>
				) : (
					<Table
						size="small"
						rowKey="device_id"
						columns={columns}
						dataSource={data?.rows ?? []}
						pagination={{ pageSize: 25, hideOnSinglePage: true }}
						scroll={{ x: 1200 }}
						bordered
						locale={{ emptyText: <Empty description="Nothing written off" /> }}
					/>
				)}
			</div>
		</AppLayout>
	);
}
